import { useEffect, useMemo, useRef, useState } from "react";
import { api, isDemo } from "../api";
import type { Project, UploadCandidate } from "../types";
import { countdown, formatBytes, MAX_PERSON_BYTES, MAX_PROJECT_BYTES, normalizePhone, parseAuditionFilename, sha256 } from "../utils";

const CONTACT_STORAGE_KEY = "cmvfilm-audition-contact-v1";
const CONTACT_TTL_MS = 30 * 24 * 60 * 60 * 1000;
const MASCOT_STORAGE_KEY = "cmvfilm-daily-mascots-v2";

function dailyMascot(identity: { phone: string; wechatId: string; wechatNickname: string }, current: number) {
  const day = new Date().toLocaleDateString("en-CA"); const normalized = { phone: identity.phone.trim(), wechatId: identity.wechatId.trim().toLowerCase(), wechatNickname: identity.wechatNickname.trim().toLowerCase() };
  try {
    const records = JSON.parse(localStorage.getItem(MASCOT_STORAGE_KEY) || "[]") as Array<typeof normalized & { day: string; index: number }>;
    const matches = (record: typeof records[number]) => Boolean((normalized.phone && record.phone === normalized.phone) || (normalized.wechatId && record.wechatId === normalized.wechatId) || (normalized.wechatNickname && record.wechatNickname === normalized.wechatNickname));
    const saved = records.find(matches);
    if (saved?.day === day && Number.isInteger(saved.index) && saved.index >= 0 && saved.index < 7) return saved.index;
    const previous = saved && Number.isInteger(saved.index) ? saved.index : current; let next = Math.floor(Math.random() * 6); if (next >= previous) next += 1;
    const updated = [...records.filter((record) => !matches(record)), { ...normalized, day, index: next }].slice(-100); localStorage.setItem(MASCOT_STORAGE_KEY, JSON.stringify(updated)); return next;
  } catch { return current; }
}

function loadRecentContact() {
  try {
    const saved = JSON.parse(localStorage.getItem(CONTACT_STORAGE_KEY) || "null");
    if (!saved || Date.now() > Number(saved.expiresAt)) return null;
    return { wechatNickname: String(saved.wechatNickname || ""), wechatId: String(saved.wechatId || ""), phone: String(saved.phone || "") };
  } catch { return null; }
}

function saveRecentContact(fields: { wechatNickname: string; wechatId: string; phone: string }) {
  try { localStorage.setItem(CONTACT_STORAGE_KEY, JSON.stringify({ ...fields, expiresAt: Date.now() + CONTACT_TTL_MS })); } catch { /* Private browsing or storage disabled. */ }
}

function buildCandidate(file: File): UploadCandidate {
  const parsed = parseAuditionFilename(file.name);
  return { file, roleName: parsed.roleName, cvAbbr: parsed.cvAbbr, valid: parsed.valid, error: parsed.error, progress: 0 };
}

function publicDescription(value: string) {
  return value
    .replace(/角色名\s*[_+\-]?\s*CV缩写/g, "角色名 + 空格/下划线/短横线/加号 + CV名字或缩写")
    .replace(/MP3\s*[、\/]\s*WAV/gi, "MP3")
    .replace(/WAV\s*[、\/]\s*MP3/gi, "MP3")
    .replace(/MP3\s*或\s*WAV/gi, "MP3")
    .replace(/WAV\s*或\s*MP3/gi, "MP3")
    .replace(/[，,；;、]?\s*单人累计(?:不超过|不能超过)\s*10\s*(?:Mi?B|M)[。.]?/gi, "")
    .replace(/([；;，,])\s*([。.!！])/g, "$2")
    .trim();
}

function uploadWithProgress(upload: { url: string; method?: string; fields?: Record<string, string>; contentType: string }, file: File, onProgress: (percent: number) => void) {
  return new Promise<void>((resolve, reject) => {
    const method = upload.method || "PUT"; const xhr = new XMLHttpRequest(); xhr.open(method, upload.url);
    if (method === "PUT") xhr.setRequestHeader("Content-Type", upload.contentType);
    xhr.upload.onprogress = (event) => { if (event.lengthComputable) onProgress(Math.round(event.loaded / event.total * 100)); };
    xhr.onerror = () => reject(new Error(`${file.name} 上传网络中断`));
    xhr.onload = () => xhr.status >= 200 && xhr.status < 300 ? resolve() : reject(new Error(`${file.name} 上传失败`));
    if (method === "POST") { const form = new FormData(); for (const [key, value] of Object.entries(upload.fields || {})) form.append(key, value); form.append("file", file); xhr.send(form); }
    else xhr.send(file);
  });
}

async function playDuplicateProgress(update: (progress: number) => void) {
  for (const progress of [30, 42, 55, 68, 80, 90, 100]) {
    await new Promise((resolve) => setTimeout(resolve, 120));
    update(progress);
  }
}

export function PublicUpload({ token }: { token: string }) {
  const [project, setProject] = useState<Project | null>(null);
  const [fields, setFields] = useState({ wechatNickname: "", wechatId: "", phone: "" });
  const [files, setFiles] = useState<UploadCandidate[]>([]);
  const retainedFiles = useRef<UploadCandidate[]>([]);
  const [dragging, setDragging] = useState(false);
  const [busy, setBusy] = useState(false);
  const [message, setMessage] = useState("");
  const [receipt, setReceipt] = useState("");
  const [mascotIndex, setMascotIndex] = useState(() => Math.floor(Math.random() * 7));
  const input = useRef<HTMLInputElement>(null);

  const updateFiles = (update: UploadCandidate[] | ((current: UploadCandidate[]) => UploadCandidate[])) => {
    setFiles((current) => {
      const next = typeof update === "function" ? update(current) : update;
      retainedFiles.current = next;
      return next;
    });
  };

  useEffect(() => {
    const recent = loadRecentContact(); if (recent) setFields(recent);
    api.publicProject(token).then(setProject).catch((e) => setMessage(e.message));
  }, [token]);
  const total = files.reduce((sum, item) => sum + item.file.size, 0);
  const personLimit = project?.maxPersonBytes || MAX_PERSON_BYTES;
  const allValid = files.length > 0 && files.every((item) => item.valid) && total <= personLimit && (!project || (project.bytes || 0) + total <= MAX_PROJECT_BYTES);
  const isClosed = project ? new Date(project.deadline).getTime() <= Date.now() : false;
  const roles = useMemo(() => [...new Set(files.filter((f) => f.valid).map((f) => f.roleName))], [files]);
  const overallProgress = files.length ? Math.round(files.reduce((sum, item) => sum + item.progress, 0) / files.length) : 0;

  const addFiles = (list: FileList | File[]) => {
    setReceipt(""); setMessage("");
    updateFiles((current) => {
      const known = new Set(current.map((item) => `${item.file.name}:${item.file.size}:${item.file.lastModified}`));
      return [...current, ...Array.from(list).filter((file) => !known.has(`${file.name}:${file.size}:${file.lastModified}`)).map(buildCandidate)];
    });
  };

  const submit = async () => {
    const phone = normalizePhone(fields.phone);
    if (!fields.wechatNickname.trim() || !fields.wechatId.trim() || !/^1\d{10}$/.test(phone)) {
      updateFiles([...retainedFiles.current]);
      setMessage("请完整填写微信昵称、微信号和11位手机号"); return;
    }
    saveRecentContact({ wechatNickname: fields.wechatNickname.trim(), wechatId: fields.wechatId.trim(), phone });
    if (!allValid) { setMessage(project && (project.bytes || 0) + total > MAX_PROJECT_BYTES ? "本项目已达到 500M 上传容量上限，无法继续上传" : total > personLimit ? `所选文件总量超过本项目 ${(personLimit / 1024 / 1024).toFixed(0)}MB 限制，请减少后再提交` : "请先修正文件问题"); return; }
    setMascotIndex(dailyMascot({ phone, wechatId: fields.wechatId, wechatNickname: fields.wechatNickname }, mascotIndex));
    setBusy(true); setMessage("");
    try {
      const hashed = await Promise.all(files.map(async (item) => ({ ...item, sha256: await sha256(item.file) })));
      updateFiles(hashed.map((item) => ({ ...item, progress: 20 })));
      if (isDemo) {
        for (let p = 35; p <= 100; p += 13) { await new Promise((r) => setTimeout(r, 90)); updateFiles((old) => old.map((item) => ({ ...item, progress: Math.min(100, p) }))); }
        setReceipt((await api.submitDemo()).receiptId);
      } else {
        const initResponse = await fetch(`${import.meta.env.VITE_API_BASE}/public/projects/${token}/uploads/init`, {
          method: "POST", headers: { "Content-Type": "application/json" }, body: JSON.stringify({ ...fields, phone, files: hashed.map((item) => ({ name: item.file.name, size: item.file.size, type: item.file.type, sha256: item.sha256, roleName: item.roleName, cvAbbr: item.cvAbbr })) }),
        });
        const init = await initResponse.json(); if (!initResponse.ok) throw new Error(init.message);
        const hasNewUpload = init.uploads.some((upload: { duplicate: boolean }) => !upload.duplicate);
        if (!hasNewUpload) {
          // Keep the friendly progress animation on repeat clicks, but never send
          // an already accepted audio object to OSS again.
          await playDuplicateProgress((progress) => updateFiles((current) => current.map((item) => ({ ...item, progress }))));
        }
        for (const upload of init.uploads) {
          if (upload.duplicate) continue;
          const candidate = hashed.find((item) => item.sha256 === upload.sha256)!;
          await uploadWithProgress(upload, candidate.file, (progress) => updateFiles((current) => current.map((item) => item.sha256 === upload.sha256 ? { ...item, progress } : item)));
        }
        updateFiles((current) => current.map((item) => ({ ...item, progress: 100 })));
        const completed = await fetch(`${import.meta.env.VITE_API_BASE}/public/projects/${token}/uploads/complete`, { method: "POST", headers: { "Content-Type": "application/json" }, body: JSON.stringify({ uploadId: init.uploadId }) }).then((r) => r.json());
        setReceipt(completed.receiptId);
      }
    } catch (e) { setMessage(e instanceof Error ? e.message : "上传失败"); }
    finally { setBusy(false); }
  };

  if (!project) return <main className="public-shell"><div className="loading-card">正在读取试音项目…</div></main>;
  if (isClosed) return <main className="public-shell"><section className="closed-card"><div className="closed-icon">✓</div><h1>本项目已截止</h1><p>{project.name}</p><span>感谢关注，请留意宠魅声后续试音。</span></section></main>;

  return <main className="public-shell">
    <header className="public-brand public-brand-text"><strong>宠魅声试音收集</strong><span>Audition Collection</span></header>
    <section className="project-hero">
      <div className="eyebrow"><span className="live-dot"/>正在征集试音</div>
      <h1>{project.name}</h1>
      <div className="deadline-row"><span>截止时间</span><strong>{new Date(project.deadline).toLocaleString("zh-CN", { month: "long", day: "numeric", hour: "2-digit", minute: "2-digit" })}</strong><em>{countdown(project.deadline)}</em></div>
    </section>
    <section className="upload-card">
      <section className="public-step-card contact-step">
      <div className="section-heading"><span>01</span><div><h2>填写联系信息</h2><p>仅用于本次试音联络，不会公开展示</p></div></div>
      <div className="form-grid">
        <label>微信昵称<input required value={fields.wechatNickname} onChange={(e) => setFields({ ...fields, wechatNickname: e.target.value })} placeholder="请输入微信昵称" maxLength={40}/></label>
        <label><span className="field-label">微信号 <em className="field-note">（请务必正确填写）</em></span><input required value={fields.wechatId} onChange={(e) => setFields({ ...fields, wechatId: e.target.value })} placeholder="请输入微信号" maxLength={64}/></label>
        <label className="full">手机号<input required inputMode="numeric" value={fields.phone} onChange={(e) => setFields({ ...fields, phone: e.target.value })} placeholder="请输入11位手机号" maxLength={11}/></label>
      </div>
      </section>
      <section className="public-step-card audio-step">
      <div className="section-heading"><span>02</span><div><h2>上传试音音频</h2><p>可一次拖入多个角色的试音音频</p></div></div>
      <div className="audio-naming-reminder"><strong>命名提醒</strong><span>请按“角色名 + CV缩写”的格式命名；仅接收 MP3</span><code>角色名_CV缩写.mp3</code></div>
      <div className={`dropzone ${dragging ? "dragging" : ""}`} onClick={() => input.current?.click()} onDragOver={(e) => { e.preventDefault(); setDragging(true); }} onDragLeave={() => setDragging(false)} onDrop={(e) => { e.preventDefault(); setDragging(false); addFiles(e.dataTransfer.files); }}>
        <input ref={input} type="file" accept=".mp3,audio/mpeg" multiple hidden onChange={(e) => e.target.files && addFiles(e.target.files)}/>
        <div className="upload-glyph">↑</div><strong>拖拽音频到这里，或点击选择</strong><span>仅支持 MP3</span>
      </div>
      {files.length > 0 && <div className="file-list">
        <div className="file-summary"><span>已选择 {files.length} 个文件 · 识别 {roles.length} 个角色</span><b className={total > personLimit || (project.bytes || 0) + total > MAX_PROJECT_BYTES ? "danger" : ""}>合计 {formatBytes(total)}</b></div>
        {files.map((item, index) => <div className={`file-row ${item.valid ? "" : "invalid"}`} key={`${item.file.name}-${index}`}>
          <div className="file-type">MP3</div>
          <div className="file-info"><strong>{item.file.name}</strong>{item.valid ? <span><i>{item.roleName}</i> · CV {item.cvAbbr} · {formatBytes(item.file.size)}</span> : <span className="danger">{item.error}</span>}{item.progress > 0 && <div className="mini-progress"><i style={{ width: `${item.progress}%` }}/></div>}</div>
          <button type="button" className="icon-button" onClick={(e) => { e.preventDefault(); e.stopPropagation(); updateFiles(files.filter((_, i) => i !== index)); }} aria-label="移除文件">×</button>
        </div>)}
      </div>}
      {message && <div className="alert error">{message}</div>}
      {receipt && <div className={`success-box success-box-centered mascot-${mascotIndex}`}><span>✓</span><b>试音提交成功！</b><div className="success-mascot-cheer" aria-hidden="true"><span>必中！</span><div className="upload-mascot"/></div></div>}
      <div className={`upload-action-wrap mascot-${mascotIndex}`}>
        {busy && <div className="upload-mascot-lane" style={{ "--upload-progress": `${overallProgress}%` } as React.CSSProperties} aria-live="polite">
          <div className="upload-mascot-runner" style={{ left: `calc(${overallProgress}% - ${overallProgress * .78}px)` }} aria-hidden="true"><span>必中！</span><div className="upload-mascot"/></div>
          <div className="upload-percent">{overallProgress}%</div>
        </div>}
        <button type="button" className="primary wide" disabled={busy || !allValid} onClick={(event) => { event.preventDefault(); void submit(); }}>{busy ? "正在安全上传…" : "确认提交试音"}</button>
      </div>
      <p className="audition-result-note">中音会直接微信联系，未中音默认不通知，感谢老师们！</p>
      </section>
    </section>
    <footer>© 2026 宠魅声工作室 · cmvfilm.cn</footer>
  </main>;
}
